import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, X, Search } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Compact chip-style multi-select filter. Click → popover with searchable
 * checkbox list. Trigger label intelligently summarises the selection
 * ("All Stages" → "Phase 2 + 1" → "3 stages selected").
 *
 * Props:
 *   label          — base label e.g. "All Stages"
 *   pluralName     — used in trigger summary, e.g. "stages"
 *   options        — Array<string | { value, label, hint? }>
 *   value          — Array<string>
 *   onChange       — (newValues: string[]) => void
 *   width          — Tailwind width class (default w-44)
 *   testId         — base data-testid
 *   searchable     — show search input above options (default auto: true if >8 options)
 */
export default function MultiSelectFilter({
  label = 'All',
  pluralName = 'items',
  options = [],
  value = [],
  onChange,
  width = 'w-44',
  testId = 'multiselect',
  searchable,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  // Normalize options to {value, label, hint} shape.
  const normOptions = options.map(o =>
    typeof o === 'string' ? { value: o, label: o } : o
  );
  const showSearch = searchable === undefined ? normOptions.length > 8 : searchable;

  // Close on outside click. Uses `click` event (not `mousedown`) and a
  // double-RAF activation so the click that opened us doesn't immediately
  // close us, and stopPropagation on option clicks keeps the popover open
  // through multiple selections.
  useEffect(() => {
    if (!open) return undefined;
    let active = false;
    const handler = (e) => {
      if (!active) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => { active = true; });
    });
    document.addEventListener('click', handler);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('click', handler);
    };
  }, [open]);

  const toggle = (v, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (value.includes(v)) onChange(value.filter(x => x !== v));
    else onChange([...value, v]);
  };
  const clearAll = (e) => {
    e?.stopPropagation();
    onChange([]);
  };

  const filtered = query
    ? normOptions.filter(o =>
        (o.label || '').toLowerCase().includes(query.toLowerCase())
      )
    : normOptions;

  // Trigger label
  let triggerLabel = label;
  if (value.length === 1) {
    const o = normOptions.find(x => x.value === value[0]);
    triggerLabel = o?.label || value[0];
  } else if (value.length === 2) {
    const labels = value.map(v => {
      const o = normOptions.find(x => x.value === v);
      return o?.label || v;
    });
    triggerLabel = labels.join(', ');
  } else if (value.length > 2) {
    triggerLabel = `${value.length} ${pluralName} selected`;
  }

  return (
    <div ref={containerRef} className={`relative ${width}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full h-9 px-3 text-sm bg-white border rounded-md flex items-center justify-between gap-1 transition-colors ${
          value.length > 0
            ? 'border-[#00709B]/40 bg-[#00709B]/5 text-[#00709B]'
            : 'border-slate-200 text-slate-700 hover:border-slate-300'
        }`}
        data-testid={`${testId}-trigger`}
      >
        <span className="truncate text-left">{triggerLabel}</span>
        <span className="flex items-center gap-1 shrink-0">
          {value.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={clearAll}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearAll(e); }}
              className="text-slate-400 hover:text-rose-600 -mr-1"
              data-testid={`${testId}-clear`}
              aria-label="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 flex flex-col"
          data-testid={`${testId}-popover`}
        >
          {showSearch && (
            <div className="px-2 pt-2 pb-1">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={`Search ${pluralName}…`}
                  className="w-full pl-7 pr-2 h-7 text-xs border border-slate-200 rounded outline-none focus:border-[#00709B]/40"
                  data-testid={`${testId}-search`}
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">No matches</div>
            ) : (
              filtered.map(o => {
                const checked = value.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={(e) => toggle(o.value, e)}
                    className="w-full px-2 py-1.5 hover:bg-slate-50 text-left text-xs flex items-center gap-2"
                    data-testid={`${testId}-opt-${o.value}`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                      checked ? 'bg-[#00709B] border-[#00709B] text-white' : 'border-slate-300'
                    }`}>
                      {checked && <Check className="w-3 h-3" />}
                    </span>
                    <span className="truncate flex-1 text-slate-700">{o.label}</span>
                    {o.hint && (
                      <span className="text-[10px] text-slate-400 shrink-0">{o.hint}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {value.length > 0 && (
            <div className="border-t border-slate-100 px-2 py-1.5 flex items-center justify-between bg-slate-50">
              <span className="text-[10px] text-slate-500">{value.length} selected</span>
              <Button variant="ghost" size="sm" onClick={() => onChange([])}
                className="h-6 px-2 text-[10px] text-slate-600 hover:text-rose-600"
                data-testid={`${testId}-clear-all`}>
                Clear
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
